import './Classes.scss'

import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, Input, Modal, notification, Row, Select, Table } from 'antd'
import DeleteModal from 'components/okulyonetim-admin/delete-modal/DeleteModal'
import { saveAs } from 'file-saver'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import { useOkulSiniflariQuery, useSinifSilMutation } from '../../../../gql/GeneratedGraphqlTypes'
import Spinner from '../../../spinner/Spinner'
import { ReactComponent as AktarIcon } from '../okul-siparis-page/icons/aktar.svg'
import { ReactComponent as FalseIcon } from './icons/false.svg'
import { ReactComponent as TrueIcon } from './icons/true.svg'
const { Option } = Select
const { Search } = Input

const Classes: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedDurum, setSelectedDurum] = useState<string | undefined>(undefined)
  const { okul } = useContext(AppContext)
  const [sinifSilMutation] = useSinifSilMutation()
  const { data, loading, refetch, error } = useOkulSiniflariQuery({
    variables: {
      sube_id: okul?.id,
      arama: searchValue || null,
      sinif_sube_id: selectedDurum ?? null,
    },
    fetchPolicy: 'network-only',
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [modalMessage, setModalMessage] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const showDeleteModal = (ids: string[]) => {
    setDeleteId(ids.join(','))
    setIsDeleteModalVisible(true)
  }

  const handleDelete = async (ids: string) => {
    const idList: number[] = ids.split(',').map((id) => Number(id))
    try {
      const response = await sinifSilMutation({
        variables: {
          id: idList,
        },
      })

      if (response.data?.sinifSil.success && !response.data.sinifSil.silinemeyenler?.length) {
        notification.success({
          message: '',
          description: `${response.data?.sinifSil?.message}`,
          duration: 6,
        })
        setIsDeleteModalVisible(false)
        setSelectedRowKeys([])

        await refetch()
      } else {
        setModalTitle('Silinemeyen Sınıflar')

        setIsModalOpen(true)

        const silinenler = response.data?.sinifSil?.silinenler?.map((item) => `✅ Silindi: ${item?.isim}`) ?? []

        // Silinemeyenler Listesi
        const silinemeyenler =
            response.data?.sinifSil?.silinemeyenler?.map((item) => {
              const nedenMesaji = item?.neden?.length
                  ? item.neden.map((neden) => {
                    const sinifDersIdMatch = /SınıfDers ID: (\d+)/.exec(neden!)
                    const sinifDersId = sinifDersIdMatch?.[1]

                    if (sinifDersId) {
                      return (
                          `❌ <a  href="/v1/okulyonetimadmin/sinifdersi/${sinifDersId}/" target="_blank" rel="noopener noreferrer">${neden}</a>`
                      )
                    }

                    return `❌ ${neden}`
                  }).join('<br/>')
                  : ''

              return nedenMesaji
            }) ?? []

        //  Modal Mesajını Güncelle
        setModalMessage([...silinenler, ...silinemeyenler])
      }
    } catch (error) {
      notification.error({
        message: '',
        description: 'Listeyi gönderirken bir hata oluştu.',
        duration: 6,
      })
    }
  }

  // Filtreleme değiştikçe veriyi tekrar çek
  useEffect(() => {
    refetch()
  }, [searchValue, selectedDurum, refetch])

  const handleSearch = () => {
    setSearchValue(searchTerm)
  }

  if (loading) {
    return <Spinner showSpinner={loading} color="red" />
  }

  if (error) {
    console.error(error)
    navigate('/')
    return null
  }
  const handleFilterChange = (filterType: 'durum', value: string | undefined) => {
    if (value === null) {
      setSelectedDurum(value) // "Tümü" seçildiğinde filtreyi kaldır
    } else {
      setSelectedDurum(value)
    }
  }

  const filteredData = data?.okulSiniflari?.siniflar?.filter(Boolean) ?? []
  const totalItems = filteredData.length
  const displayedItems = selectedRowKeys.length
  const siparisDurumOptions = [
    <Option key="tum" value={null}>
      Tümü
    </Option>,
    ...(data?.okulSiniflari?.sinif_kategorileri?.map((durum) => (
      <Option key={durum?.id} value={durum?.id}>
        {durum?.isim}
      </Option>
    )) || []),
  ]

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
    },
    {
      title: 'İsim',
      dataIndex: 'isim',
      key: 'isim',
      sorter: (a: any, b: any) => (a.isim ?? '').localeCompare(b.isim ?? ''),
    },
    {
      title: 'Sınıf Şubesi',
      dataIndex: 'sinif_sube',
      key: 'sinif_sube',
      sorter: (a: any, b: any) => (a.sinif_sube ?? '').localeCompare(b.sinif_sube ?? ''),
    },
    {
      title: 'Kampüs',
      dataIndex: 'kampus',
      key: 'kampus',
      sorter: (a: any, b: any) => (a.kampus ?? '').localeCompare(b.kampus ?? ''),
    },
    {
      title: 'Aktif',
      width: 80,
      dataIndex: 'aktif',
      render: (_: string, record: any) => {
        return record.aktif ? <TrueIcon /> : <FalseIcon />
      },
      key: 'aktif',
    },
    {
      title: 'Mevcut',
      dataIndex: 'mevcut',
      key: 'mevcut',
      sorter: (a: any, b: any) => a.mevcut - b.mevcut,
    },
    {
      title: '',
      dataIndex: 'detay',
      key: 'detay',
      render: (_: string, record: any) => (
        <Button
          className="okul-yonetim-admin-detail-button"
          onClick={() => navigate(`/okulyonetimadmin/sinif/${record.id}`)}
        >
          Detay
        </Button>
      ),
    },
  ]
  const handleCancel = () => {
    setIsModalOpen(false)
  }
  const exportAllToExcel = () => {
    const worksheetData: any[] = []

    const headers = ['id','isim', 'kampus_id', 'kampus_adi', 'kategori_id', 'kategori_adi', 'mevcut', 'aktif']
    worksheetData.push(headers)

    data?.okulSiniflari?.siniflar.forEach((siniflar: any) => {
      const transformedData = [
        siniflar.id || '',
        siniflar.isim || '',
        siniflar.kampus_id || '',
        siniflar.kampus || '',
        siniflar.kategori_id || '',
        siniflar.sinif_sube || '',
        siniflar.mevcut || '',
        siniflar.aktif ? 'Aktif' : 'Pasif',
      ]
      worksheetData.push(transformedData)
    })

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

    const colWidths = worksheetData[0].map((_: string, colIndex: number) =>
      Math.max(...worksheetData.map((row) => (row[colIndex] ? row[colIndex].toString().length : 10))),
    )
    worksheet['!cols'] = colWidths.map((width: number) => ({ wch: width }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([excelBuffer]), `siniflar.xlsx`)
  }

  return (
    <div className="okul-sinif-page">
      <div className="okul-sepeti-admin-header">
        <h1>Sınıflar </h1>
        <div className="export-buttons">
          <Button onClick={exportAllToExcel} className="export-button">
            <AktarIcon /> Dışa Aktar{' '}
          </Button>
          <Button onClick={() => navigate('/okulyonetimadmin/sinif/sinif-ice-aktar')} className="export-button">
            <AktarIcon /> İçe Aktar
          </Button>
        </div>
      </div>
      <Row justify={'space-between'} gutter={8} className="filter-section">
        <Row gutter={12} style={{ width: '90%' }}>
          <Col span={8}>
            <Search
              width={'100%'}
              placeholder="Sınıf Ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              allowClear
              enterButton={
                <div className="search-suffix-div">
                  Ara <SearchOutlined />{' '}
                </div>
              }
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Sınıf Şubesi"
              value={selectedDurum}
              onChange={(value) => handleFilterChange('durum', value)}
              allowClear
            >
              {siparisDurumOptions}
            </Select>
          </Col>
        </Row>
        <div className="button-right">
          <Button onClick={() => navigate('/okulyonetimadmin/sinif/sinif-ekle')}>Sınıf Ekle</Button>
        </div>
      </Row>

      <Table
        dataSource={filteredData as object[]}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          total: totalItems,
          showTotal: (total) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                style={{
                  display: selectedRowKeys.length === 0 ? 'none' : '',
                }}
                icon={<DeleteOutlined />}
                onClick={() => showDeleteModal(selectedRowKeys.map((id) => id.toString()))}
                type="primary"
                danger
              >
                Seçilen {selectedRowKeys.length} Sınıfları Sil
              </Button>

              <span style={{ marginLeft: '10px' }}>
                {' '}
                {/* Buton ile metin arasına boşluk koyuyoruz */}
                Seçilen: {displayedItems} / Toplam: {total}
              </span>
            </div>
          ),
          onChange: (page, size) => {
            setCurrentPage(page)
            setPageSize(size)
          },
        }}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
        loading={loading}
        rowKey="id"
        className="okul-yonetim-admin-table"
        rowSelection={{
          type: 'checkbox',
          ...Selection,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
      />
      <DeleteModal
        title="Seçilen Sınıfları Sil"
        id={deleteId || ''}
        visible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onDelete={handleDelete}
      />
      <Modal
        footer={<Button onClick={handleCancel}>Kapat</Button>}
        onCancel={handleCancel}
        title={modalTitle}
        open={isModalOpen}
      >
        <ul>
          {modalMessage.map((message, index) => (
              <li key={index} dangerouslySetInnerHTML={{__html: message}}/>
          ))}
        </ul>
      </Modal>
    </div>
  )
}

export default Classes
